import React, {useMemo} from 'react'
import {useTranslation} from '@lib/intl/useTranslations'
import {useRouter} from 'next/router'
import Link from 'next/link'
import {toast} from 'react-toastify'

import {getLinkUrl, URL_PORTFOLIO} from '@constant/url'
import {createLog} from '@util/logs'

import useLogin from '@hook/useLogin'
import useCopy from '@hook/useCopy'
import useQueryFetchMe from '@hook/query/account/useQueryFetchMe'

import Text from '@component/Text/Text'
import TagText from '@component/Text/TagText'
import ProTag from '@component/tags/ProTag'
import WizThumbnailChangeInput from '@component/inputs/WizThumbnailChangeInput'
import UserThumbnail from '@component/images/UserThumbnail'
import WizToastMessage from '@component/snackbar/WizToastMessage'

import IconNoticeRed from '@svg/common/ic_notice_red.svg'
import IconShare from '@svg/common/ic_btn_share_v2.svg'
import IconNext from '@svg/common/ic_btn_next.svg'
import IconAccountPortfolio from '@svg/gnb/ic_account_portfolio.svg'

interface IProps {
    className?: string
    alertLength?: number
    openAlert?: () => void
    openNoRegisteredApiModal?: () => void
    openSharePortfolioOptionModal?: () => void
}

const UserAccountInfo = ({
    className,
    alertLength,
    openAlert,
    openNoRegisteredApiModal,
    openSharePortfolioOptionModal,
}: IProps) => {
    const t = useTranslation()
    const router = useRouter()
    const {copyToClipboard} = useCopy()

    const {data: me} = useQueryFetchMe(undefined, false)
    const {isMe} = useLogin(me?.id)

    const userLocalName = useMemo(() => {
        return me?.nicknames?.find(item => item.language === router?.locale)?.nickname ?? me?.name
    }, [me, router?.locale])

    const onClickSharePortfolio = () => {
        if (!me?.keys || me?.keys?.length < 1) {
            openNoRegisteredApiModal?.()
        } else {
            if (isMe) {
                createLog('event', 'key_portfolio_sidebar_open_share_portfolio_option_modal')
                openSharePortfolioOptionModal?.()
            } else {
                createLog('event', 'key_portfolio_sidebar_share_portfolio_copy_link')
                void copyToClipboard(`https://leagueoftraders.io/ko/user/${me?.id}/portfolio`)
                toast(<WizToastMessage text={t('common.copied')} />)
            }
        }
    }

    const openFilePicker = () => {
        const fileInput = document.getElementById('thumb-file-input')
        if (fileInput) {
            setTimeout(() => fileInput?.click(), 0)
        }
    }

    return (
        <div
            className={`bg-gray09 dark:bg-bg_dark_white02 p-[20px] pt-[30px] rounded-tr-[5px] rounded-tl-[5px] ${className}`}>
            <div className={'flex items-center gap-x-[10px] mb-[15px]'}>
                <div>
                    <UserThumbnail
                        src={me?.thumbnail}
                        size={50}
                        isPro={me?.is_pro}
                        containerClassName={'w-[50px] flex-none'}
                    />

                    <WizThumbnailChangeInput downViewAlign={'left'} onCustomChangeImage={openFilePicker} />
                </div>

                <div>
                    <Text className={'text-ti2 text-gray01 dark:text-dark_gray01 break-all'}>
                        {userLocalName}
                        {me?.is_pro && <ProTag size={'small'} className={'ml-[5px]'} type={'text'} />}
                    </Text>
                    <div
                        onClick={onClickSharePortfolio}
                        className={'flex items-center mt-[3px] gap-x-[3px] cursor-pointer'}>
                        <IconShare className={'w-[12px] fill-gray02 dark:fill-dark_gray02'} />
                        <Text className={'underline text-body3 text-gray02 dark:text-dark_gray02'}>
                            {t('portfolio.sidebar.share.portfolio.short')}
                        </Text>
                    </div>
                </div>
            </div>

            {alertLength > 0 ? (
                <div
                    onClick={openAlert}
                    className={
                        'cursor-pointer mb-[10px] items-center flex gap-x-[5px] p-[10px] rounded-[5px] ring-[1px] ring-red_tint01 dark:ring-dark_red_tint01 bg-red_tint02 dark:bg-dark_red_tint02'
                    }>
                    <IconNoticeRed className={'flex-none fill-red_shade dark:fill-dark_red_shade w-[14px]'} />
                    <TagText className={'text-body3 text-gray01 dark:text-dark_gray01 grow'}>
                        {t('accountAlert.counts', {count: alertLength})}
                    </TagText>
                    <IconNext className={'w-[12px] fill-gray02 dark:fill-dark_gray02 flex-none'} />
                </div>
            ) : null}

            <Link href={getLinkUrl(URL_PORTFOLIO(me?.id))} className={'max-w-[180px] mx-auto block'}>
                <button
                    className={`w-full bg-white dark:bg-bg_dark_white03 py-[10px] gap-x-[5px] ring-[1px] ring-gray06 dark:ring-dark_gray06 rounded-[20px] mx-auto justify-center flex items-center hover:bg-gray09 dark:hover:bg-dark_gray09 active:bg-gray07 dark:active:bg-dark_gray07`}>
                    <IconAccountPortfolio className={'w-[12px] fill-gray01 dark:fill-dark_gray01'} />
                    <Text
                        enablePreWhiteSpace={false}
                        className={'text-body3 whitespace-nowrap text-gray01 dark:text-dark_gray01'}>
                        {t('mobileAccountNavigation.myPortfolio')}
                    </Text>
                </button>
            </Link>
        </div>
    )
}
export default UserAccountInfo
